import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { LbOptions, LbPartData } from '@uefacom-newdev/liveblog-integration-react';
import clsx from 'clsx';
import { useState, useMemo, useEffect } from 'react';

import { getPollDetailQuery, getPollQuery } from '../queries/polls';

import { sendPollVote } from '@apis/polls';
import { trackCardElement } from '@services/GoogleTagManagerService';
import { IS_TEST } from '@utilities/browser';
import { getCookie, setCookie } from '@utilities/cookies';

import * as classes from './styles.module.scss';

const defaults = { pollRefreshRate: 30000, expires: 365, className: classes.poll };

const PollVoted = ({ poll, voted, options }) => {
  const { data: votes } = useQuery({
    ...getPollDetailQuery(poll.id),
    enabled: !!poll.id && !!voted,
    refetchInterval: !IS_TEST && options.pollRefreshRate,
  });

  const getPerc = (id: string): number => votes?.answers?.find((x) => x.id === id)?.perc;

  return poll?.answers?.filter(Boolean).map((answer) => {
    const perc = getPerc(answer.id);
    const style = { width: `${perc}%` };

    return (
      <div
        className={clsx({
          [classes.answer]: true,
          [classes.answerVoted]: voted === answer.id,
        })}
        key={answer.id}
      >
        <div className={classes.resultBar} style={style} />
        <div className={classes.answerText}>{answer.text}</div>
        <div className={classes.answerPerc}>{perc}%</div>
      </div>
    );
  });
};

const PollNoVoted = ({ poll, sendVote }) => {
  const handleClick = (answerId: string) => () => {
    sendVote(answerId);
  };
  return (
    <>
      {poll?.answers?.filter(Boolean).map((answer) => (
        <button type='button' className={classes.answer} key={answer.id} onClick={handleClick(answer.id)}>
          <div className={classes.answerText}>{answer.text}</div>
        </button>
      ))}
    </>
  );
};

export const Poll = ({
  data,
  options,
  isMatchEvent,
}: {
  data: LbPartData;
  options: LbOptions;
  isMatchEvent?: boolean;
}) => {
  const queryClient = useQueryClient();
  const [voted, setVoted] = useState<string>();

  options = { ...defaults, ...options };
  const id = data.Id ?? (data as any).id;

  const pollQuery = getPollQuery(id);
  const { data: poll } = useQuery(pollQuery);

  const { mutate: sendVote } = useMutation({
    mutationFn: (answerId: string) => sendPollVote({ answerId, pollId: poll?.pollId, nodeId: poll?.id }),
    onSuccess: (resp) => {
      const pollId = resp?.poll?.pollId;
      const isVoted = resp?.user?.voted;
      if (pollId) {
        setCookie(pollId, id, { expires: options.expires });
      }
      if (isVoted) {
        setVoted(id);
      }
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: pollQuery.queryKey });
    },
  });

  const trackingData = useMemo(
    () => ({
      category: 'poll card',
      name: `Poll - ${poll?.question}`,
      id: isMatchEvent ? options?.match?.id : options?.article?.id,
    }),
    [isMatchEvent, options?.article?.id, options?.match?.id, poll?.question],
  );

  useEffect(() => {
    const myVote = !!poll?.pollId && getCookie(poll.pollId);
    setVoted(myVote);
  }, [poll]);

  return (
    <div className={clsx(options.className, classes.poll)} ref={trackCardElement(trackingData)}>
      <div className={clsx({ [classes.body]: true, [classes.voted]: voted })}>
        <div className={classes.question}>{poll?.question}</div>
        {voted ? (
          <PollVoted poll={poll} voted={voted} options={options} />
        ) : (
          <PollNoVoted poll={poll} sendVote={sendVote} />
        )}
      </div>
    </div>
  );
};
